import React from 'react';

import { useClassnames } from '@lumapps/classnames';
import { Attributes } from '@lumapps/data-attributes';
import { ColorPalette, Text, type TextProps, Theme } from '@lumapps/lumx/react';

import { useBlockVideoContext } from '../BlockVideoContext';
import { CLASSNAME as BLOCK_VIDEO_CLASSNAME } from '../constants';
import type { Video } from '../types';

import './index.scss';

const CLASSNAME = `${BLOCK_VIDEO_CLASSNAME}-button` as const;

interface ButtonProps {
    /** List of attributes that can be added to the button. */
    attributes?: Attributes;
    /** Component children */
    children?: React.ReactNode;
    /** Function called when the button is clicked */
    onClick: (video: Video) => void;
    /**
     * Control whether the text should truncate or not.
     * Setting as `true` will make the text truncate on a single line.
     * Setting as `{ lines: number }` will make the text truncate on a multiple lines.
     */
    truncate?: TextProps['truncate'];
    /** This stretches the link's layout over the closest parent with a relative position */
    isStretched?: boolean;
}

/**
 * This component is a vanilla button with no style applied.
 * It is useful to bind other components and make them interactive.
 */
export const Button = ({ children, onClick, truncate, isStretched, ...attributes }: ButtonProps) => {
    const { block } = useClassnames(CLASSNAME);
    const { isLoading, video, theme } = useBlockVideoContext();

    /**
     * ✅ We don’t want to return a button until the component has loaded
     * We simply return children which should take care of their skeletons
     */
    if (isLoading) {
        return <Text as="span">{children}</Text>;
    }

    const title = typeof video.title === 'string' ? video.title : video.title?.value;

    return (
        // eslint-disable-next-line react/forbid-elements
        <button
            {...attributes}
            className={block({
                dark: theme === Theme.light,
                light: theme === Theme.dark,
                'is-truncated': Boolean(truncate),
                'is-stretched': Boolean(isStretched),
            })}
            type="button"
            onClick={() => onClick?.(video)}
        >
            <Text as="span" truncate={truncate} color={theme === Theme.light ? ColorPalette.dark : ColorPalette.light}>
                {children
                    ? React.Children.map(
                          children,
                          (child) =>
                              React.isValidElement<{ isInteractive: boolean }>(child) &&
                              React.cloneElement(child, { isInteractive: true }),
                      )
                    : title}
            </Text>
        </button>
    );
};
